import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { sceneState } from "@/scene/sceneState";
import { useReducedMotion } from "@/hooks/useReducedMotion";

gsap.registerPlugin(ScrollTrigger);

/**
 * Breakpoint-aware scroll choreography.
 * Uses gsap.matchMedia() to tailor movement per device:
 *   - Desktop (≥900px): Full cinematic camera moves + bigger waves.
 *   - Tablet (600–899px): Moderate camera moves.
 *   - Mobile (<600px): Subtle, energy-efficient movement.
 *   - Reduced-motion: nearly static, calm ocean.
 */
export function ScrollChoreographer() {
  const reduced = useReducedMotion();
  const ctxRef = useRef(null);

  useEffect(() => {
    if (reduced) {
      Object.assign(sceneState.camera, { posX: 0, posY: 2.1, posZ: 8.2, lookY: 0.6 });
      Object.assign(sceneState.waves, {
        bigElevation: 0.22,
        bigFrequencyX: 1.0,
        bigFrequencyZ: 0.8,
        bigSpeed: 0.25,
        smallElevation: 0.08,
        smallFrequency: 2.4,
        smallSpeed: 0.18,
      });
      return;
    }

    const mm = gsap.matchMedia();

    mm.add(
      {
        // Each media query defines per-breakpoint tweaks
        isDesktop: "(min-width: 900px)",
        isTablet: "(min-width: 600px) and (max-width: 899px)",
        isMobile: "(max-width: 599px)",
      },
      (context) => {
        const { isDesktop, isTablet, isMobile } = context.conditions;

        // Per-breakpoint configuration
        const config = isDesktop
          ? {
              hero: { posX: 0, posY: 2.2, posZ: 9.2, lookY: 0.6, logoY: 1.25, logoScale: 1.0 },
              about: { posX: -0.6, posY: 1.95, posZ: 8.0, lookY: 0.45, logoY: 1.5, logoScale: 0.95 },
              contact: { posX: 0.1, posY: 2.55, posZ: 6.8, lookY: 0.8, logoY: 2.0, logoScale: 0.7 },
              waveBoost: 1.0,
              scrub: 1.2,
            }
          : isTablet
            ? {
                hero: { posX: 0, posY: 2.35, posZ: 9.8, lookY: 0.7, logoY: 1.2, logoScale: 1.0 },
                about: { posX: -0.3, posY: 2.1, posZ: 8.6, lookY: 0.55, logoY: 1.4, logoScale: 0.9 },
                contact: { posX: 0.05, posY: 2.7, posZ: 7.4, lookY: 0.85, logoY: 1.9, logoScale: 0.75 },
                waveBoost: 0.85,
                scrub: 1.0,
              }
            : {
                // mobile
                hero: { posX: 0, posY: 2.45, posZ: 10.6, lookY: 0.75, logoY: 1.1, logoScale: 0.95 },
                about: { posX: -0.15, posY: 2.25, posZ: 9.8, lookY: 0.6, logoY: 1.3, logoScale: 0.9 },
                contact: { posX: 0.02, posY: 2.8, posZ: 8.8, lookY: 0.9, logoY: 1.8, logoScale: 0.8 },
                waveBoost: 0.6,
                scrub: 0.8,
              };

        // Initialize scene with hero state
        sceneState.camera.posX = config.hero.posX;
        sceneState.camera.posY = config.hero.posY;
        sceneState.camera.posZ = config.hero.posZ;
        sceneState.camera.lookY = config.hero.lookY;
        sceneState.logo.posY = config.hero.logoY;
        sceneState.logo.scale = config.hero.logoScale;

        // Proxy for GSAP tweening (mirrored to sceneState on each frame)
        const proxy = {
          posX: sceneState.camera.posX,
          posY: sceneState.camera.posY,
          posZ: sceneState.camera.posZ,
          lookY: sceneState.camera.lookY,

          bigElevation: sceneState.waves.bigElevation * config.waveBoost,
          bigFrequencyX: sceneState.waves.bigFrequencyX,
          bigFrequencyZ: sceneState.waves.bigFrequencyZ,
          bigSpeed: sceneState.waves.bigSpeed,
          smallElevation: sceneState.waves.smallElevation * config.waveBoost,
          smallFrequency: sceneState.waves.smallFrequency,
          smallSpeed: sceneState.waves.smallSpeed,

          pDrift: sceneState.particles.drift,
          pOpacity: sceneState.particles.opacity,

          logoY: sceneState.logo.posY,
          logoZ: sceneState.logo.posZ,
          logoRotY: sceneState.logo.rotY,
          logoScale: sceneState.logo.scale,
          logoOpacity: sceneState.logo.opacity,

          fogNear: sceneState.fog.near,
          fogFar: sceneState.fog.far,
        };

        const apply = () => {
          sceneState.camera.posX = proxy.posX;
          sceneState.camera.posY = proxy.posY;
          sceneState.camera.posZ = proxy.posZ;
          sceneState.camera.lookY = proxy.lookY;

          sceneState.waves.bigElevation = proxy.bigElevation;
          sceneState.waves.bigFrequencyX = proxy.bigFrequencyX;
          sceneState.waves.bigFrequencyZ = proxy.bigFrequencyZ;
          sceneState.waves.bigSpeed = proxy.bigSpeed;
          sceneState.waves.smallElevation = proxy.smallElevation;
          sceneState.waves.smallFrequency = proxy.smallFrequency;
          sceneState.waves.smallSpeed = proxy.smallSpeed;

          sceneState.particles.drift = proxy.pDrift;
          sceneState.particles.opacity = proxy.pOpacity;

          sceneState.logo.posY = proxy.logoY;
          sceneState.logo.posZ = proxy.logoZ;
          sceneState.logo.rotY = proxy.logoRotY;
          sceneState.logo.scale = proxy.logoScale;
          sceneState.logo.opacity = proxy.logoOpacity;

          sceneState.fog.near = proxy.fogNear;
          sceneState.fog.far = proxy.fogFar;
        };

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: "#scroll-root",
            start: "top top",
            end: "bottom bottom",
            scrub: config.scrub,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              sceneState.progress = self.progress;
              apply();
            },
          },
        });

        // Hero → About: tide rises + camera drifts
        tl.to(
          proxy,
          {
            posX: config.about.posX,
            posY: config.about.posY,
            posZ: config.about.posZ,
            lookY: config.about.lookY,
            bigElevation: 0.55 * config.waveBoost,
            smallElevation: 0.18 * config.waveBoost,
            bigSpeed: 0.5,
            smallSpeed: 0.35,
            logoY: config.about.logoY,
            logoScale: config.about.logoScale,
            logoRotY: 0.15,
            logoOpacity: 0.8,
            pDrift: 0.07,
            pOpacity: 0.95,
            fogNear: 6,
            fogFar: 42,
            ease: "none",
          },
          0,
        );

        // About → Contact: calm, horizon opens
        tl.to(
          proxy,
          {
            posX: config.contact.posX,
            posY: config.contact.posY,
            posZ: config.contact.posZ,
            lookY: config.contact.lookY,
            bigElevation: 0.26 * config.waveBoost,
            smallElevation: 0.09 * config.waveBoost,
            bigSpeed: 0.3,
            smallSpeed: 0.22,
            logoY: config.contact.logoY,
            logoScale: config.contact.logoScale,
            logoRotY: -0.05,
            logoOpacity: 0.55,
            pDrift: 0.035,
            pOpacity: 0.7,
            fogNear: 10,
            fogFar: 60,
            ease: "none",
          },
          0.55,
        );

        // Cleanup when this matchMedia context ends
        return () => {
          tl.kill();
        };
      },
    );

    ctxRef.current = mm;

    const refreshSoon = () => {
      setTimeout(() => ScrollTrigger.refresh(), 200);
    };
    window.addEventListener("load", refreshSoon);

    return () => {
      window.removeEventListener("load", refreshSoon);
      mm.revert();
    };
  }, [reduced]);

  return null;
}
